import { z } from 'zod';

const channelEnum = z.enum(['whatsapp', 'email']);
const variantEnum = z.enum(['pf', 'pj']).nullable().optional();

export const createNotificationTemplateSchema = z
  .object({
    key: z
      .string()
      .min(2)
      .max(100)
      .regex(/^[a-z0-9_]+$/, 'key deve ser snake_case'),
    name: z.string().min(2).max(200),
    channel: channelEnum,
    variant_type: variantEnum,
    subject: z.string().max(500).nullable().optional(),
    body_html: z.string().nullable().optional(),
    body_text: z.string().min(1).max(10_000),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.channel === 'email' && !val.subject) {
      ctx.addIssue({
        code: 'custom',
        path: ['subject'],
        message: 'Subject obrigatório pra canal email',
      });
    }
  });

export const updateNotificationTemplateSchema = z
  .object({
    name: z.string().min(2).max(200).optional(),
    subject: z.string().max(500).nullable().optional(),
    body_html: z.string().nullable().optional(),
    body_text: z.string().min(1).max(10_000).optional(),
    is_active: z.boolean().optional(),
  })
  .strict()
  .refine((val) => Object.keys(val).length > 0, {
    message: 'Pelo menos um campo deve ser fornecido',
  });

export const previewNotificationTemplateSchema = z
  .object({
    sample_client_id: z.string().uuid(),
    sample_charge_id: z.string().uuid().optional(),
    sample_subscription_id: z.string().uuid().optional(),
  })
  .strict();

export type CreateNotificationTemplateDto = z.input<typeof createNotificationTemplateSchema>;
export type UpdateNotificationTemplateDto = z.input<typeof updateNotificationTemplateSchema>;
export type PreviewNotificationTemplateDto = z.input<typeof previewNotificationTemplateSchema>;

// ============================================================================
// Response schemas (story 6.9 — frontend admin UI)
// ============================================================================

export const notificationTemplateListItemSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  channel: z.string(),
  variantType: z.string().nullable(),
  version: z.number().int(),
  isActive: z.boolean(),
  updatedAt: z.coerce.date(),
});

export const notificationTemplateListResponseSchema = z.object({
  items: z.array(notificationTemplateListItemSchema),
});

export const notificationTemplateDetailSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  channel: z.string(),
  variantType: z.string().nullable(),
  subject: z.string().nullable(),
  bodyHtml: z.string().nullable(),
  bodyText: z.string(),
  version: z.number().int(),
  isActive: z.boolean(),
});

export const notificationTemplateMutationResponseSchema = z.object({
  id: z.string().uuid(),
  key: z.string().optional(),
  version: z.number().int(),
});

export const notificationTemplatePreviewResponseSchema = z.object({
  subject: z.string().nullable(),
  bodyText: z.string(),
  bodyHtml: z.string().nullable(),
});

export type NotificationTemplateListItem = z.output<typeof notificationTemplateListItemSchema>;
export type NotificationTemplateListResponse = z.output<
  typeof notificationTemplateListResponseSchema
>;
export type NotificationTemplateDetail = z.output<typeof notificationTemplateDetailSchema>;
export type NotificationTemplateMutationResponse = z.output<
  typeof notificationTemplateMutationResponseSchema
>;
export type NotificationTemplatePreviewResponse = z.output<
  typeof notificationTemplatePreviewResponseSchema
>;
